import {
  Add,
  Attachment,
  ContentCopy,
  Delete,
  DragIndicator,
  Edit,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNotification } from '../../../hooks/NotificationContext';
import {
  changeStepNumber,
  cloneTestSteps,
  deleteTestStep,
  getAllTestSteps,
} from '../../../modules/teststep';
import DetailsCard from '../../../ui/layout/DetailsCard';
import ActionMenu from '../../../ui/primitives/ActionMenu';
import TestStepAttachments from './TestStepAttachments';
import TestStepsFormModal from './TestStepsFormModal';
import { isExecutiveManager } from '../../../lib/userUtils';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModel';
import { CloneChip, CountChip } from '../../../ui/primitives/Chips';


const COLUMNS = [
  { id: 'sNo', label: '#', width: '64px' },
  { id: 'step', label: 'Description', width: '36%' },
  { id: 'testData', label: 'Test Data', width: '22%' },
  { id: 'expectedResult', label: 'Expected Result', width: '22%' },
];
const URI_COLUMN = { id: 'uri', label: 'URI', width: '14%' };

// ─── Styled components ────────────────────────────────────────────────────────

const CellText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'muted',
})(({ theme, muted }) => ({
  color: muted ? theme.palette.text.disabled : theme.palette.text.primary,
  fontStyle: muted ? 'italic' : 'normal',
  fontSize: '0.8125rem',
  lineHeight: 1.6,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

CellText.defaultProps = { variant: 'body2' };

const BodyCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1.375),
  paddingBottom: theme.spacing(1.375),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  verticalAlign: 'top',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeadCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.03)
      : alpha(theme.palette.grey[900], 0.025),
  borderBottom: `2px solid ${theme.palette.divider}`,
  '&:first-of-type': { borderLeft: 'none' },
}));

const AttachmentIcon = styled(Attachment)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.primary.main,
  opacity: 0.75,
  cursor: 'pointer',
  transition: 'opacity 0.15s',
  '&:hover': { opacity: 1 },
}));

const AddFirstStepLink = styled(Box)(({ theme }) => ({
  display: 'inline',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  fontWeight: 600,
  '&:hover': { textDecoration: 'underline' },
}));

const LoadingRows = ({ count = 5, cols }) =>
  Array.from({ length: count }).map((_, i) => (
    <TableRow key={i}>
      {cols.map((col) => (
        <BodyCell key={col.id}>
          <Skeleton variant="text" width="70%" height={18} />
        </BodyCell>
      ))}
      <BodyCell />
    </TableRow>
  ));


const INITIAL_SCRIPT = { step: '', testData: '', expectedResult: '' };

const TestStepsContainer = ({ data, testCaseId }) => {
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [selectedScript, setSelected] = useState(INITIAL_SCRIPT);
  const [mode, setMode] = useState('add');
  const [formData, setFormData] = useState({ scripts: [] });
  const [openAttachments, setOpenAtt] = useState(false);
  const [totalScripts, setTotal] = useState(0);
  const [openConfirmation, setConfirm] = useState(false);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isReordering, setIsReordering] = useState(false);

  const tableContainerRef = useRef(null);
  const { addNotification } = useNotification();

  const technology_layer = data?.technology_layer;
  const isViewer = isExecutiveManager();
  const isDragDisabled = isViewer || !!data?.is_archived || isReordering;
  const columns = technology_layer === 'API/Integration' ? [...COLUMNS, URI_COLUMN] : COLUMNS;

  const fetchScripts = async () => {
    try {
      const response = await getAllTestSteps(testCaseId);
      if (response?.rows && Array.isArray(response.rows)) {
        setTotal(response.rows.length);
        const sorted = response.rows
          .map((s) => ({
            testData: s.test_data,
            expectedResult: s.expected_result,
            step: s.step,
            attachments: s.attachments,
            id: s.id,
            uri: s.uri,
            is_cloned: s.is_cloned,
            sNo: s.s_no,
          }))
          .sort((a, b) => a.sNo - b.sNo);
        setFormData({ scripts: sorted });
      }
    } catch {
      addNotification('danger', 'Failed to fetch test steps.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    if (data?.id) fetchScripts();
  }, [data?.id]);

  const handleClose = () => { setShow(false); setSelected(INITIAL_SCRIPT); };
  const handleEdit = (row) => { setMode('edit'); setSelected(row); setShow(true); };
  const handleAdd = () => { setMode('add'); setSelected(INITIAL_SCRIPT); setShow(true); };
  const handleAttachment = (row) => { setSelected(row); setOpenAtt(true); };
  const handleOpenConfirm = (row) => { setSelected(row); setConfirm(true); };
  const handleConfirmClose = () => { setSelected(null); setConfirm(false); };

  const handleClone = async (row) => {
    const { created_at, updated_at, attachments, ...rest } = row;
    try {
      await cloneTestSteps({ testStep: { ...rest, isCloning: row?.id } }, testCaseId);
      addNotification('success', 'Test step cloned successfully!');
      fetchScripts();
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to clone test step.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTestStep(selectedScript?.id);
      addNotification('success', 'Test step deleted successfully!');
      handleConfirmClose();
      fetchScripts();
    } catch (err) {
      addNotification('danger', err?.message || 'Failed to delete test step.');
    }
  };

  const getMenuItems = (row) => [
    { label: 'Edit', icon: <Edit />, onClick: () => handleEdit(row), hidden: isViewer },
    { label: 'Clone', icon: <ContentCopy />, onClick: () => handleClone(row), hidden: isViewer },
    {
      label: row?.attachments?.length > 0 ? 'View Attachments' : 'Add Attachment',
      icon: <Attachment />,
      onClick: () => handleAttachment(row),
    },
    {
      label: 'Delete', icon: <Delete />, onClick: () => handleOpenConfirm(row),
      color: 'error', hidden: isViewer,
    },
  ];

  const handleDragStart = (e, index) => {
    if (isDragDisabled || loading) { e.preventDefault(); return; }
    setOriginalOrder([...formData.scripts]);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.style.cssText =
      'background:#f5f5f5;padding:6px 12px;border-radius:4px;font-size:0.85rem;font-weight:600;';
    ghost.textContent = `Step ${formData.scripts[index].sNo}`;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);

    // Auto-scroll near edges
    if (tableContainerRef.current) {
      const THRESHOLD = 50, SPEED = 10;
      const { clientY } = e;
      const rect = tableContainerRef.current.getBoundingClientRect();
      if (clientY < rect.top + THRESHOLD) tableContainerRef.current.scrollTop -= SPEED;
      else if (clientY > rect.bottom - THRESHOLD) tableContainerRef.current.scrollTop += SPEED;
    }
  };

  const handleDragLeave = (e) => {
    if (e.target === tableContainerRef.current) setDragOverIndex(null);
  };

  const handleDragEnd = () => { setDraggedIndex(null); setDragOverIndex(null); };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null); setDragOverIndex(null); return;
    }
    const updated = [...formData.scripts];
    const [dragged] = updated.splice(draggedIndex, 1);
    updated.splice(dropIndex, 0, dragged);
    const reordered = updated.map((s, idx) => ({ ...s, sNo: idx + 1 }));
    setFormData({ scripts: reordered });
    setDraggedIndex(null); setDragOverIndex(null);
    setIsReordering(true);
    try {
      await changeStepNumber(reordered);
      addNotification('success', 'Test steps reordered successfully!');
      fetchScripts();
    } catch (err) {
      setFormData({ scripts: originalOrder });
      addNotification('danger', err?.message || 'Failed to reorder test steps.');
    } finally {
      setIsReordering(false);
    }
  };


  const cardTitle = (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography variant="h6">Test Steps</Typography>
      {!loading && <CountChip label={totalScripts} />}
    </Stack>
  );

  const cardActions = !isViewer && (
    <Button variant="contained" startIcon={<Add />} onClick={handleAdd} disabled={data?.is_archived}>
      Add Step
    </Button>
  );

  return (
    <>
      <DetailsCard title={cardTitle} actions={cardActions}>
        <TableContainer ref={tableContainerRef} className="test-scripts-table-container">
          <Table size="small" sx={{ minWidth: 640, tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <HeadCell key={col.id} sx={{ width: col.width }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}
                    >
                      {col.label}
                    </Typography>
                  </HeadCell>
                ))}
                <HeadCell sx={{ width: '52px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <LoadingRows count={4} cols={columns} />

              ) : formData.scripts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={{ border: 'none' }}>
                    <Box sx={{ py: 7, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No test steps yet.{' '}
                        {!isViewer && !data?.is_archived && (
                          <AddFirstStepLink component="span" onClick={handleAdd}>
                            Add the first step.
                          </AddFirstStepLink>
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>

              ) : (
                formData.scripts.map((row, rowIndex) => {
                  const isDragging = draggedIndex === rowIndex;
                  const isDragOver = dragOverIndex === rowIndex;

                  return (
                    <TableRow
                      key={row.id}
                      draggable={!isDragDisabled}
                      hover
                      aria-grabbed={isDragging}
                      aria-dropeffect={!isDragDisabled ? 'move' : 'none'}
                      onDragStart={(e) => handleDragStart(e, rowIndex)}
                      onDragOver={(e) => handleDragOver(e, rowIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, rowIndex)}
                      onDragEnd={handleDragEnd}
                      sx={{
                        cursor: !isDragDisabled ? 'grab' : 'default',
                        '&:active': !isDragDisabled ? { cursor: 'grabbing' } : {},
                        backgroundColor: isDragOver
                          ? (t) => alpha(t.palette.primary.main, 0.06)
                          : 'inherit',
                        opacity: isDragging ? 0.5 : 1,
                        transition: 'background-color 0.15s ease, opacity 0.15s ease',
                        '&:last-child td': { borderBottom: 'none' },
                        ...(isDragOver && {
                          boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                        }),
                      }}
                    >
                      <BodyCell sx={{ width: '64px' }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <DragIndicator
                            fontSize="small"
                            sx={{
                              color: isDragDisabled ? 'text.disabled' : 'text.secondary',
                              opacity: isDragDisabled ? 0.4 : 0.7,
                              fontSize: '1rem',
                              flexShrink: 0,
                            }}
                          />
                          <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1 }}
                            >
                              {row.sNo}
                            </Typography>
                            {row.is_cloned && <CloneChip label="clone" size="small" />}
                          </Stack>
                        </Stack>
                      </BodyCell>

                      <BodyCell><CellText>{row.step}</CellText></BodyCell>
                      <BodyCell>
                        {row.testData
                          ? <CellText>{row.testData}</CellText>
                          : <CellText muted>—</CellText>}
                      </BodyCell>
                      <BodyCell>
                        {row.expectedResult
                          ? <CellText>{row.expectedResult}</CellText>
                          : <CellText muted>—</CellText>}
                      </BodyCell>
                      {technology_layer === 'API/Integration' && (
                        <BodyCell>
                          {row.uri
                            ? <CellText>{row.uri}</CellText>
                            : <CellText muted>—</CellText>}
                        </BodyCell>
                      )}
                      <BodyCell align="right" sx={{ width: '52px', px: 1, verticalAlign: 'top' }}>
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.25}>
                          {row.attachments?.length > 0 && (
                            <Tooltip
                              title={`${row.attachments.length} attachment${row.attachments.length > 1 ? 's' : ''}`}
                            >
                              <AttachmentIcon onClick={() => handleAttachment(row)} />
                            </Tooltip>
                          )}
                          <ActionMenu items={getMenuItems(row)} tooltip="Row actions" />
                        </Stack>
                      </BodyCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DetailsCard>
      <TestStepsFormModal
        show={show}
        handleClose={handleClose}
        script={selectedScript}
        fetchScripts={fetchScripts}
        testCaseId={data?.id}
        addNotification={addNotification}
        mode={mode}
        totalScripts={totalScripts}
        technology_layer={technology_layer}
      />

      <TestStepAttachments
        show={openAttachments}
        handleClose={() => setOpenAtt(false)}
        testStepId={selectedScript?.id}
        selectedScript={selectedScript}
        fetchScripts={fetchScripts}
      />

      <ConfirmationModal
        show={openConfirmation}
        handleConfirm={handleDelete}
        handleClose={handleConfirmClose}
        title="Remove Test Step"
        body={
          <Typography variant="body2">
            Are you sure you want to remove{' '}
            <strong>step {selectedScript?.sNo}</strong>? This will also remove
            any attachments or execution results associated with it.
          </Typography>
        }
        buttonLabel="Yes, Remove"
        buttonLabelSecondary="Cancel"
      />
    </>
  );
};

export default TestStepsContainer;